import { z } from 'zod';

export const templateOptionsV1Schema = z.array(
  z.object({
    id: z.string(),
    value: z.string(),
  }),
);

export type TemplateOptions = z.infer<typeof templateOptionsV1Schema>;

export const NOTE_WIDTHS = ['sm', 'md', 'lg'] as const;
export type NoteWidth = (typeof NOTE_WIDTHS)[number];

export const NOTE_FIELD_TYPES = [
  'inputText',
  'select',
  'checkbox',
  'radio',
] as const;
export type NoteFieldType = (typeof NOTE_FIELD_TYPES)[number];

const NESTABLE_NOTE_FIELD_TYPES = ['checkbox', 'radio'] as const;

type NestableNoteFieldType = (typeof NESTABLE_NOTE_FIELD_TYPES)[number];

export const canNestNoteSettings = (
  type: NoteFieldType,
): type is NestableNoteFieldType =>
  NESTABLE_NOTE_FIELD_TYPES.some((nestable) => nestable === type);

const nestedNoteSettingSchema = z.object({
  optionId: z.string(),
  required: z.boolean(),
  width: z.enum(NOTE_WIDTHS),
});

export type NestedNoteSetting = z.infer<typeof nestedNoteSettingSchema>;

const noteSettingObjectSchema = z.object({
  optionId: z.string(),
  type: z.enum(NOTE_FIELD_TYPES).default('inputText'),
  required: z.boolean(),
  width: z.enum(NOTE_WIDTHS),
  options: templateOptionsV1Schema.default([]),
  noteSettings: z.array(nestedNoteSettingSchema).default([]),
});

export const noteSettingSchema = noteSettingObjectSchema.transform((data) => {
  if (data.type === 'inputText') {
    return { ...data, options: [], noteSettings: [] };
  }

  if (data.type === 'select') {
    return { ...data, noteSettings: [] };
  }

  return data;
});

export type NoteSetting = z.infer<typeof noteSettingSchema>;

const noteLeafValueSchema = z.string();

const noteSelectValueSchema = z.object({
  value: z.string(),
  note: z.string(),
});

const noteCheckboxValueSchema = z.array(
  z.object({
    id: z.string(),
    value: z.string(),
    note: z.string(),
  }),
);

export const noteValueSchema = z.union([
  noteLeafValueSchema,
  noteSelectValueSchema,
  noteCheckboxValueSchema,
]);

export type NoteValue = z.infer<typeof noteValueSchema>;
